/*
 * The field wrapper — replaces Bootstrap's `.form-group` (just the gap below each
 * field). Invalid state comes from Base UI's `data-invalid` (auto-set on the root),
 * NOT Bootstrap `.has-error`, so label/control redden via the module rules below.
 */
.root {
	margin-bottom: var(--form-group-margin);
}

/*
 * The field's label — replaces Bootstrap's `.control-label`, replicated from the
 * theme (weight 400, 13/20 type, 7px bottom-margin, inline-block, inherits #000).
 * Goes red when the surrounding field is invalid (Base UI `data-invalid`).
 */
.label {
	display: inline-block;
	max-width: 100%;
	margin-bottom: var(--padding-base-y);
	font-size: var(--font-size-base);
	line-height: var(--line-height-base);
	font-weight: normal;
	color: inherit;
}

.root[data-invalid] .label {
	color: var(--state-danger-text);
}

/*
 * Muted helper text. Replicates the theme's `.help-block` look (12px, muted) but
 * stays grey even while the field is invalid — unlike Bootstrap's `.help-block`,
 * which `.has-error` turns red. A description is neutral info; only the error
 * message should go red. Bootstrap can't express "grey regardless of .has-error"
 * without an override, so this is a CSS module rather than the Bootstrap class.
 * Colour comes from the LESS→CSS-var token bridge (no hardcoded hex).
 */
.description {
	margin-top: var(--padding-base-y);
	margin-bottom: 0; /* reset the browser's default <p> bottom margin */
	font-size: var(--font-size-small);
	line-height: 1.53846;
	color: var(--text-muted);
}

/*
 * Supporting-text line (description or error) — one reserved line below the input.
 * The error REPLACES the description (Material-style): when an error is showing, the
 * description is hidden and the error takes its place, so a field is never taller
 * for having both, and the message line stays a single line.
 */

/* Error shown → hide the description so the error takes its place. `[data-slot="field-error"]`
   only exists while an error renders, so the description shows normally otherwise. */
.root:has([data-slot="field-error"]) .description {
	display: none;
}

/*
 * `flow-root` contains the message's top margin so it can't collapse out. When the
 * field has a description, that description already reserves the message line (the
 * error replaces it), so the slot needs no reservation. When there's NO description,
 * the slot reserves one line itself so an appearing error doesn't shift siblings.
 */
.slot {
	display: flow-root;
}

.root:not(:has([data-slot="field-description"])) .slot {
	min-height: 25px; /* one 12px/1.5 line + the 7px gap below the control */
}

/*
 * Replicates the theme's `.help-block` error text (12px, danger red). The error
 * only ever renders when the field is invalid, so it is always the danger colour
 * (the theme's `@state-danger-text`, via the token bridge). 7px top gap matches the
 * description so replacing one with the other doesn't move anything.
 */
.error {
	margin-top: var(--padding-base-y);
	font-size: var(--font-size-small);
	line-height: 1.5;
	color: var(--state-danger-text);
}

/*
 * `.label` rendered as the <legend> of a grouped field — `Field.Root
 * render={<fieldset/>}` around a Radio/CheckboxGroup with `Field.Label
 * render={<legend/>} nativeLabel={false}`. The `.label` base already sets the
 * font/size/margin/colour, so here we only neutralise the native <legend> chrome
 * the browser/theme add (padding, bottom border, uppercase) — leaving a legend
 * that reads exactly like a stacked label (and still reddens via `.root[data-invalid]`).
 */
legend.label {
	padding: 0;
	border: 0;
	text-transform: none;
}

/*
 * A single checkbox/radio that is its own field: `.label` encloses the control
 * and its text (web-standard <label> wrapping). Lay it out as an inline row at the
 * canonical control-to-text gap and drop the stacked bottom-margin. Scoped to a
 * label that actually contains a Base UI checkbox/radio, so it never touches
 * ordinary stacked labels or a group's legend.
 */
.label:has([data-slot="checkbox"], [data-slot="radio-group-item"]) {
	display: inline-flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 0;
	cursor: pointer;
}
